import React, { useState } from "react";
import type { IUser } from "../../types/iuser";
import useDashboard from "../../hooks/useDashboard";
import UserConversations from "./user_conversations";
import ScriptTemplatesTable from "./script_templates_table";
import SettingsModal, { type SettingsTab } from "../shared/settings_modal";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  StatCard,
  GreetingCard,
  OverviewRow,
  AsideRow,
  ListCard,
  type GreetingCardProps,
  type ListCardItem,
  type BreadcrumbItem,
  astryx,
  neutralTheme,
  useDocumentTheme,
  Bell,
  CreditCard,
  Contact,
  ChevronRight,
  UsersRound,
  Settings,
  Sparkles,
  Plus,
} from "@shadowcue/ui";

interface DashboardProps {
  currentUser: IUser;
}

const Dashboard = (props: DashboardProps) => {
  const mode = useDocumentTheme();
  const [conversationsPage, setConversationsPage] = useState(1);
  // null keeps the dialog closed; a tab name opens it there.
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
  const { dashboard, loading, error } = useDashboard(
    props.currentUser.authToken,
    conversationsPage,
  );

  const breadcrumbs: BreadcrumbItem[] = [{ label: "Dashboard", href: "/dashboard" }];

  const headerDescription = props.currentUser.admin
    ? "An overview of your organization's screening activity."
    : "An overview of your screening activity.";

  // Admins come here to build scripts; everyone else to read conversations.
  const greeting: GreetingCardProps = props.currentUser.admin
    ? {
        description: "Create a script and AI drafts the questions.",
        actionLabel: "New script",
        actionHref: "/admin/script_templates/new",
        actionIcon: Plus,
      }
    : {
        description: "Pick up where you left off.",
        actionLabel: "View conversations",
        actionHref: "/dashboard/conversations",
      };

  const chevron = <astryx.Icon icon={ChevronRight} />;
  const comingSoon = <astryx.Badge variant="neutral" label="Soon" />;

  const settingsItems: ListCardItem[] = [
    {
      icon: Settings,
      label: "General",
      endContent: chevron,
      onClick: () => setSettingsTab("general"),
    },
    {
      icon: Sparkles,
      label: "LLM Configuration",
      endContent: chevron,
      onClick: () => setSettingsTab("llm"),
    },
    { icon: UsersRound, label: "Users", endContent: chevron, href: "/admin/users" },
    { icon: Contact, label: "Contacts", endContent: chevron, href: "/admin/contacts" },
    { icon: CreditCard, label: "Billing & Plan", endContent: comingSoon },
    { icon: Bell, label: "Notifications", endContent: comingSoon },
  ];

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={loading}>
          <PageHeader title="Dashboard" description={headerDescription} />

          <astryx.VStack gap={6}>
            <OverviewRow feature={<GreetingCard {...greeting} />}>
              {dashboard && (
                <>
                  <StatCard
                    label="Scripts"
                    value={dashboard.activeScriptsCount}
                    sub="Active"
                    valueAlign="center"
                  />
                  <StatCard
                    label="Conversations"
                    value={dashboard.conversationsCount}
                    sub="All time"
                    valueAlign="center"
                  />
                </>
              )}
            </OverviewRow>

            {error && (
              <astryx.Banner
                status="error"
                title="Couldn't load your dashboard"
                description="Something went wrong while loading."
                endContent={
                  <astryx.Button
                    label="Try again"
                    variant="secondary"
                    onClick={() => window.location.reload()}
                  />
                }
              />
            )}

            <ScriptTemplatesTable currentUser={props.currentUser} />

            <AsideRow aside={<ListCard title="Settings" items={settingsItems} />}>
              {dashboard && (
                <UserConversations
                  currentUser={props.currentUser}
                  dashboard={dashboard}
                  onPageChange={setConversationsPage}
                />
              )}
            </AsideRow>
          </astryx.VStack>

          <SettingsModal
            isOpen={settingsTab !== null}
            initialTab={settingsTab ?? "general"}
            currentUser={props.currentUser}
            onClose={() => setSettingsTab(null)}
          />
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default Dashboard;
